import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { WidgetStyleProps } from '@finxone-platform/shared/sys-config-types';

@Component({
  standalone: false,
  selector: 'finx-date-picker',
  templateUrl: './finx-date-picker.component.html',
  styleUrls: ['./finx-date-picker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FinxDatePickerComponent),
      multi: true,
    },
  ],
  encapsulation: ViewEncapsulation.None,
})
export class FinxDatePickerComponent implements ControlValueAccessor, OnInit, OnChanges {
  private renderer = inject(Renderer2);
  el = inject(ElementRef);
  private cd = inject(ChangeDetectorRef);

  @Input() maxDate: Date;
  @Input() minDate: Date;
  @Input() showIcon = true;
  @Input() placeholder = 'Choose Date';
  @Input() dateFormat = 'yy-mm-dd';
  @Input() inputId = 'icon';
  @Input() icon = 'pi pi-calendar-plus';
  @Input() class = '';
  @Input() append = '';
  @Input() inline = false;
  @Input() ngModelValue: Date;
  @Input() defaultDate: Date;
  @Input() textColor: WidgetStyleProps;
  @Input() textInputFontStyle: WidgetStyleProps;
  @Input() textInputBackgroundColor: WidgetStyleProps;
  @Input() inputStyle: WidgetStyleProps;
  @Input() view: 'date' | 'month' | 'year' = 'date';
  @Input() selectionMode: 'single' | 'range' = 'single';

  public triggerBorderRightStyle: string;
  public triggerBorderRightWidth: string;
  public triggerBorderRightColor: string;

  @HostBinding('style.--trigger-border-right-style') get styleTriggerBorderRightStyle() {
    return this.triggerBorderRightStyle;
  }
  @HostBinding('style.--trigger-border-right-width') get styleTriggerBorderRightWidth() {
    return this.triggerBorderRightWidth;
  }
  @HostBinding('style.--trigger-border-right-color') get styleTriggerBorderRightColor() {
    return this.triggerBorderRightColor;
  }

  public readonly dateStringControl = new FormControl();
  public combinedTextStyles: WidgetStyleProps;

  public readonly formatDateFn = (date: Date) => this.formatDateWithPattern(date, this.dateFormat);

  @ViewChild('calendar', { static: true }) private calendar: ElementRef;
  // eslint-disable-next-line @typescript-eslint/ban-types, @typescript-eslint/no-empty-function
  onModelChange: Function = () => {};
  // eslint-disable-next-line @typescript-eslint/ban-types, @typescript-eslint/no-empty-function
  onModelTouched: Function = () => {};

  // eslint-disable-next-line @typescript-eslint/member-ordering
  @Output() valueChange = new EventEmitter<string>();
  @Output() datePickerChange = new EventEmitter<(string | null)[]>();
  @Output() focusEvent = new EventEmitter<boolean>();

  ngOnInit(): void {
    this.dateStringControl.valueChanges.subscribe((dateString) => {
      this.datePickerChange.emit(dateString);
      this.onModelChange(dateString);
      if (dateString) this.onModelTouched();
    });
    this.combinedTextStyles = {
      ...this.textColor,
      ...this.textInputFontStyle,
      ...this.textInputBackgroundColor,
      ...this.inputStyle,
      ...{ width: '100%' },
    };
  }

  ngOnChanges(): void {
    this.combinedTextStyles = {
      ...this.inputStyle,
      width: '100%',
    };

    const borderWidth = this.inputStyle?.['border-width'] || '1px';
    this.triggerBorderRightWidth = borderWidth;

    const borderColor = this.inputStyle?.['border-color'] || 'var(--color-border-color)';
    this.triggerBorderRightColor = borderColor;

    const borderStyle = this.inputStyle?.['border-style'] || 'solid';
    this.triggerBorderRightStyle = borderStyle;

    this.cd.detectChanges();
  }

  writeValue(value: Date | null): void {
    this.dateStringControl.setValue(value);
    this.cd.markForCheck();
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.dateStringControl.disable();
    } else {
      this.dateStringControl.enable();
    }
    this.cd.markForCheck();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSelect(e: any) {
    this.valueChange.emit(e);
  }

  // eslint-disable-next-line @angular-eslint/use-lifecycle-interface
  ngAfterViewInit() {
    if (this.textColor) {
      this.renderer.setStyle(this.calendar.nativeElement, '--date-picker-color', this.textColor['color'], 2);
    }
  }

  public onFocus = () => {
    this.focusEvent.emit(true);
  };

  public onBlur = () => {
    this.focusEvent.emit(false);
  };

  private formatDateWithPattern(date: Date, pattern: string): string {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }
    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = `${date.getFullYear()}`;
    return pattern.replace(/yy/gi, year).replace(/mm/gi, month).replace(/dd/gi, day);
  }
}
